import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import SearchRestorationStore, { MAX_BATCHES } from "../bookings/search_restoration_store"

const MAX_REPLAY_BATCHES = MAX_BATCHES
const REPLAY_REQUEST_TIMEOUT = 10_000
const SCROLL_CAPTURE_DELAY = 150
let restorationVisitIdentifier
let initialNavigationType = performance.getEntriesByType("navigation")[0]?.type

window.addEventListener("popstate", (event) => {
  restorationVisitIdentifier = event.state?.turbo?.restorationIdentifier
})

document.addEventListener("turbo:visit", (event) => {
  if (event.detail.action === "restore") {
    restorationVisitIdentifier = currentRestorationIdentifier()
  } else {
    restorationVisitIdentifier = undefined
    initialNavigationType = undefined
  }
})

export default class extends Controller {
  static targets = ["notice"]
  static values = {
    identity: String,
    restoredMessage: String,
    limitedMessage: String,
    expiredMessage: String,
    unavailableMessage: String
  }

  connect() {
    this.connected = true
    this.snapshotAvailable = this.element.dataset.bookingRestorationReady === "true"
    delete this.element.dataset.bookingRestorationReady
    this.restorationIdentifier = currentRestorationIdentifier()
    this.store = new SearchRestorationStore()
    this.batches = []
    this.replayLimited = false
    this.pendingBatch = undefined
    this.replaying = false
    this.abortController = new AbortController()
    this.captureScroll = this.captureScroll.bind(this)
    this.captureBeforeCache = this.captureBeforeCache.bind(this)
    this.captureBeforeVisit = this.captureBeforeVisit.bind(this)
    this.captureAfterMutation = this.captureAfterMutation.bind(this)
    this.handleStreamRender = this.handleStreamRender.bind(this)
    this.handleFailedBatchResponse = this.handleFailedBatchResponse.bind(this)
    this.handleBatchRequestError = this.handleBatchRequestError.bind(this)

    window.addEventListener("scroll", this.captureScroll, { passive: true })
    window.addEventListener("pagehide", this.captureBeforeVisit)
    document.addEventListener("turbo:before-cache", this.captureBeforeCache)
    document.addEventListener("turbo:before-visit", this.captureBeforeVisit)
    document.addEventListener("turbo:before-stream-render", this.handleStreamRender)
    document.addEventListener("turbo:before-fetch-response", this.handleFailedBatchResponse)
    document.addEventListener("turbo:fetch-request-error", this.handleBatchRequestError)
    this.mutationObserver = new MutationObserver(this.captureAfterMutation)
    this.mutationObserver.observe(this.element, { childList: true, subtree: true })

    this.restoreOrBegin().finally(() => {
      if (this.connected) this.element.dataset.bookingRestorationReady = "true"
    })
  }

  disconnect() {
    this.connected = false
    window.removeEventListener("scroll", this.captureScroll)
    window.removeEventListener("pagehide", this.captureBeforeVisit)
    document.removeEventListener("turbo:before-cache", this.captureBeforeCache)
    document.removeEventListener("turbo:before-visit", this.captureBeforeVisit)
    document.removeEventListener("turbo:before-stream-render", this.handleStreamRender)
    document.removeEventListener("turbo:before-fetch-response", this.handleFailedBatchResponse)
    document.removeEventListener("turbo:fetch-request-error", this.handleBatchRequestError)
    this.mutationObserver?.disconnect()
    this.abortController?.abort()
    window.clearTimeout(this.captureTimer)
  }

  recordBatch(event) {
    if (this.pendingBatch || this.replaying) return

    const batchUrl = this.validBatchUrl(event.detail.url)
    if (!batchUrl || this.batches.some((batch) => batch.url === batchUrl)) return

    this.pendingBatch = { url: batchUrl, rowCountBefore: this.rowCount() }
  }

  async restoreOrBegin() {
    const result = this.store.find(this.restorationIdentifier, this.identityValue)
    const navigationType = takeInitialNavigationType()

    if (!this.restorationNavigation(navigationType)) {
      this.begin()
      if (navigationType === "navigate" || navigationType === "reload") {
        this.holdInfiniteScroll()
        await nextRender()
        this.finishRestoration()
      }
      return
    }

    this.holdInfiniteScroll()
    if (result.status !== "found" && this.snapshotAvailable) {
      this.begin()
      await nextRender()
      await nextRender()
      this.capture()
      this.finishRestoration()
    } else if (result.status === "missing") {
      await this.startFromFirstBatch(this.unavailableMessageValue)
    } else if (result.status === "expired") {
      await this.startFromFirstBatch(this.expiredMessageValue)
    } else if (result.status === "mismatch") {
      await this.startFromFirstBatch(this.unavailableMessageValue)
    } else {
      this.batches = result.state.batches.slice()
      this.replayLimited = result.state.replayLimited

      if (this.rowCount() < result.state.rowCount) {
        await this.replay(result.state)
      } else {
        await this.restoreSnapshot(result.state)
        this.finishRestoration()
      }
    }
  }

  async startFromFirstBatch(message) {
    this.begin()
    this.showNotice(message)
    await nextRender()
    if (!this.connected) return

    window.scrollTo({ top: 0, left: 0, behavior: "instant" })
    this.capture()
    this.finishRestoration()
  }

  begin() {
    this.resetBatchHistory()
    this.capture()
  }

  async restoreSnapshot(state) {
    await this.restoreObservedPosition(state)
    this.capture()
  }

  async replay(state) {
    this.batches = []
    this.replayLimited = false
    let outcome = state.replayLimited ? "limited" : "restored"

    for (const storedBatch of state.batches.slice(0, MAX_REPLAY_BATCHES)) {
      const batchUrl = this.validBatchUrl(storedBatch.url)
      if (!batchUrl || this.rowCount() !== storedBatch.rowCountBefore) {
        outcome = "unavailable"
        break
      }

      const replayResult = await this.replayBatch(batchUrl)
      if (replayResult.reset || this.resetNoticePresent()) {
        this.resetBatchHistory()
        outcome = "unavailable"
        break
      }
      if (!replayResult.rendered || this.rowCount() !== storedBatch.rowCountAfter) {
        outcome = "unavailable"
        break
      }

      this.batches.push({ ...storedBatch, url: batchUrl })
    }

    if (outcome === "restored" && this.rowCount() < state.rowCount) outcome = "unavailable"
    if (outcome === "limited") this.replayLimited = true

    this.showNotice(this.messageFor(outcome))
    await this.restoreObservedPosition(state)
    this.capture()
    this.finishRestoration()
  }

  holdInfiniteScroll() {
    this.replaying = true
    this.element.dataset.bookingRestorationReplaying = "true"
  }

  finishRestoration() {
    if (!this.connected) return

    delete this.element.dataset.bookingRestorationReplaying
    this.replaying = false
    document.dispatchEvent(new CustomEvent("booking-restoration:finished"))
  }

  async replayBatch(url) {
    const requestController = new AbortController()
    const abortRequest = () => requestController.abort()
    const timeout = window.setTimeout(abortRequest, REPLAY_REQUEST_TIMEOUT)
    if (this.abortController.signal.aborted) {
      abortRequest()
    } else {
      this.abortController.signal.addEventListener("abort", abortRequest, { once: true })
    }

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: requestController.signal
      })
      if (!response.ok || response.redirected) return { rendered: false, reset: false }

      const message = await response.text()
      const reset = resetsBookingResults(message)
      Turbo.renderStreamMessage(message)
      await nextRender()
      return { rendered: true, reset }
    } catch {
      return { rendered: false, reset: false }
    } finally {
      window.clearTimeout(timeout)
      this.abortController.signal.removeEventListener("abort", abortRequest)
    }
  }

  restorationNavigation(navigationType) {
    const turboRestoration = restorationVisitIdentifier === this.restorationIdentifier

    if (turboRestoration) restorationVisitIdentifier = undefined
    return turboRestoration || navigationType === "back_forward"
  }

  validBatchUrl(value) {
    try {
      const url = new URL(value, window.location.origin)
      const identity = new URL(this.identityValue, window.location.origin)
      const canonicalUrl = new URL(url)
      canonicalUrl.searchParams.delete("cursor")

      if (url.origin !== window.location.origin) return
      if (!url.searchParams.has("cursor")) return
      if (canonicalUrl.pathname !== identity.pathname || canonicalUrl.search !== identity.search) return

      return url.href
    } catch {
      return
    }
  }

  captureScroll() {
    window.clearTimeout(this.captureTimer)
    this.captureTimer = window.setTimeout(() => this.capture(), SCROLL_CAPTURE_DELAY)
  }

  captureAfterMutation() {
    if (this.replaying) return

    this.commitPendingBatch()
    this.capture()
  }

  handleStreamRender(event) {
    const stream = event.target
    if (!stream.matches("turbo-stream")) return

    if (stream.matches('turbo-stream[action="replace"][target="booking_results"]')) {
      this.resetBatchHistory()
      return
    }

    if (stream.matches('turbo-stream[action="replace"][target="booking_results_pagination"]')) {
      const pendingBatch = this.pendingBatch
      if (!pendingBatch) return

      nextRender().then(() => {
        if (!this.connected || this.pendingBatch !== pendingBatch) return

        this.commitPendingBatch()
        this.clearPendingBatch()
        this.capture()
      })
    }
  }

  handleFailedBatchResponse(event) {
    const response = event.detail.fetchResponse
    if (!response.succeeded && this.pendingBatchRequest(response.location)) this.clearPendingBatch()
  }

  handleBatchRequestError(event) {
    if (this.pendingBatchRequest(event.detail.request.location)) this.clearPendingBatch()
  }

  pendingBatchRequest(value) {
    if (!this.pendingBatch) return false

    try {
      return new URL(value, window.location.origin).href === this.pendingBatch.url
    } catch {
      return false
    }
  }

  commitPendingBatch() {
    if (!this.pendingBatch) return

    const rowCountAfter = this.rowCount()
    if (rowCountAfter <= this.pendingBatch.rowCountBefore) return

    if (this.batches.length < MAX_BATCHES) {
      this.batches.push({ ...this.pendingBatch, rowCountAfter })
    } else {
      this.replayLimited = true
    }
    this.clearPendingBatch()
  }

  resetBatchHistory() {
    this.batches = []
    this.replayLimited = false
    this.clearPendingBatch()
  }

  clearPendingBatch() {
    this.pendingBatch = undefined
  }

  captureBeforeCache() {
    this.capture()
    this.element.querySelector('form[data-controller~="booking-search"]')?.reset()
  }

  captureBeforeVisit() {
    this.capture()
  }

  capture() {
    if (!this.connected || !this.restorationIdentifier || !this.hasIdentityValue) return

    this.store.save({
      identifier: this.restorationIdentifier,
      identity: this.identityValue,
      batches: this.batches,
      replayLimited: this.replayLimited,
      rowCount: this.rowCount(),
      scrollY: window.scrollY,
      anchor: this.scrollAnchor()
    })
  }

  rowCount() {
    return this.element.querySelectorAll("#booking_results_rows [data-booking-id]").length
  }

  resetNoticePresent() {
    return Boolean(this.element.querySelector('[data-testid="bookings-reset-notice"]'))
  }

  scrollAnchor() {
    const row = Array.from(this.element.querySelectorAll("#booking_results_rows [data-booking-id]"))
      .find((candidate) => candidate.getBoundingClientRect().bottom > 0)
    if (!row) return

    return {
      bookingId: row.dataset.bookingId,
      top: row.getBoundingClientRect().top
    }
  }

  async restoreObservedPosition(state) {
    await document.fonts?.ready
    for (let attempt = 0; attempt < 3; attempt += 1) {
      await nextRender()
      this.restoreScrollPosition(state.scrollY, state.anchor)
    }
  }

  restoreScrollPosition(scrollY, anchor) {
    if (!this.connected) return

    const maximum = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    window.scrollTo({ top: Math.min(scrollY, maximum), left: 0, behavior: "instant" })

    const anchorRow = anchor && this.element.querySelector(
      `#booking_results_rows [data-booking-id="${CSS.escape(anchor.bookingId)}"]`
    )
    if (anchorRow) {
      window.scrollBy({ top: anchorRow.getBoundingClientRect().top - anchor.top, left: 0, behavior: "instant" })
    }
  }

  messageFor(outcome) {
    if (outcome === "limited") return this.limitedMessageValue
    if (outcome === "unavailable") return this.unavailableMessageValue

    return this.restoredMessageValue
  }

  showNotice(message) {
    if (!this.hasNoticeTarget || !message) return

    this.noticeTarget.textContent = message
    this.noticeTarget.hidden = false
  }
}

function resetsBookingResults(message) {
  const template = document.createElement("template")
  template.innerHTML = message
  return Boolean(template.content.querySelector('turbo-stream[action="replace"][target="booking_results"]'))
}

function takeInitialNavigationType() {
  const navigationType = initialNavigationType
  initialNavigationType = undefined
  return navigationType
}

function currentRestorationIdentifier() {
  return window.history.state?.turbo?.restorationIdentifier || Turbo.session.restorationIdentifier
}

function nextRender() {
  return new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)))
}

export { MAX_REPLAY_BATCHES }
